/**
 * Directions funnel analytics — admin view of the guest en-route → PAID
 * scan pipeline. Read-only dashboard with:
 *   • Top KPI strip (pings / conversions / conversion% / flagged venues)
 *   • Daily trend chart (pings vs conversions)
 *   • Filters: time range, attribution window, sort, venue search
 *   • Per-venue leaderboard with a red badge on "smart-ass" venues
 *     (many pings, few or zero PAID scans → they may be ducking the fee)
 *   • Ghost guests table (pinged repeatedly, never showed up)
 *
 * All data comes from GET /api/admin/analytics/en-route. Every
 * interactive control has a data-testid so the testing agent can
 * drive the filters.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import {
  AlertTriangle, Ghost, Navigation, ShieldAlert, MapPin, Users,
  TrendingDown, Zap, Search, RefreshCw,
} from 'lucide-react';

import { ADMIN_PALETTE, STATUS_PALETTE } from './adminDesignSystem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGE_OPTIONS = [
  { id: '24h', label: '24h' },
  { id: '7d',  label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'all', label: 'All time' },
];

const WINDOW_OPTIONS = [
  { id: 2,  label: '2h' },
  { id: 4,  label: '4h' },
  { id: 24, label: 'Same day (24h)' },
];

const SORT_OPTIONS = [
  { id: 'pings',     label: 'Most pings' },
  { id: 'scans',     label: 'Most scans' },
  { id: 'conv_asc',  label: 'Worst conversion' },
  { id: 'conv_desc', label: 'Best conversion' },
  { id: 'flagged',   label: 'Flagged first' },
];

const cardStyle = {
  background: '#FFFFFF',
  borderRadius: 18,
  padding: 16,
  border: `1px solid ${ADMIN_PALETTE.border}`,
  boxShadow: '0 1px 2px rgba(15,17,21,0.04)',
};

const KpiCard = ({ label, value, sub, tone = 'neutral', testid }) => {
  const palette = STATUS_PALETTE[tone] || STATUS_PALETTE.neutral;
  return (
    <div data-testid={testid} style={{ ...cardStyle, padding: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: ADMIN_PALETTE.inkMuted }}>
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 26, fontWeight: 800, fontFamily: "'Inria Serif', serif", color: palette.text }}>
        {value}
      </div>
      {sub ? (
        <div style={{ marginTop: 4, fontSize: 12, color: ADMIN_PALETTE.inkMuted }}>{sub}</div>
      ) : null}
    </div>
  );
};

const FilterChips = ({ options, active, onChange, testidPrefix }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
    {options.map((opt) => {
      const isActive = String(opt.id) === String(active);
      return (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          data-testid={`${testidPrefix}-${opt.id}`}
          style={{
            padding: '6px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 700,
            border: `1px solid ${isActive ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border}`,
            background: isActive ? ADMIN_PALETTE.ink : '#FFF',
            color: isActive ? '#FFF' : ADMIN_PALETTE.inkSoft, cursor: 'pointer',
          }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const formatDateShort = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const formatDayShort = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  } catch { return iso; }
};

export const AdminEnRouteAnalyticsPanel = ({ headers }) => {
  const [range, setRange] = useState('30d');
  const [windowHours, setWindowHours] = useState(24);
  const [sort, setSort] = useState('pings');
  const [venueSearch, setVenueSearch] = useState('');
  const [flagMinPings, setFlagMinPings] = useState(5);
  const [flagMaxConvPct, setFlagMaxConvPct] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        range, window_hours: windowHours, sort,
        flag_min_pings: flagMinPings, flag_max_conv_pct: flagMaxConvPct,
      };
      if (venueSearch.trim()) params.venue_search = venueSearch.trim();
      const res = await axios.get(`${API}/admin/analytics/en-route`, { headers, params });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load Directions analytics');
    } finally {
      setLoading(false);
    }
  }, [headers, range, windowHours, sort, venueSearch, flagMinPings, flagMaxConvPct]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const chartData = useMemo(
    () => (data?.daily || []).map((row) => ({ ...row, dayLabel: formatDayShort(row.day) })),
    [data?.daily],
  );
  const venues = data?.venues || [];
  const ghosts = data?.ghost_guests_list || [];

  return (
    <div data-testid="admin-en-route-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── Header + refresh ─────────────────────────────────────── */}
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ width: 42, height: 42, borderRadius: 14, background: STATUS_PALETTE.venue.soft, color: STATUS_PALETTE.venue.text, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Navigation size={20} strokeWidth={2.2} />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 22, color: ADMIN_PALETTE.ink }}>Directions funnel</h2>
          <p style={{ margin: '2px 0 0', fontSize: 12.5, color: ADMIN_PALETTE.inkMuted, maxWidth: 720 }}>
            Guests tapping <strong>"Let venue know"</strong> vs guests who actually got a PAID QR scan.
            Flags venues that receive lots of pings but rarely process the scan — a signal they may be asking guests to skip Allure at the till.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="admin-en-route-refresh"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 9999, border: `1px solid ${ADMIN_PALETTE.border}`, background: '#FFF', color: ADMIN_PALETTE.inkSoft, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* ── Filter bar ───────────────────────────────────────────── */}
      <div style={{ ...cardStyle, display: 'grid', gap: 14 }}>
        <div style={{ display: 'grid', gap: 6 }}>
          <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: ADMIN_PALETTE.inkMuted }}>Time range</label>
          <FilterChips options={RANGE_OPTIONS} active={range} onChange={setRange} testidPrefix="admin-en-route-range" />
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: ADMIN_PALETTE.inkMuted }}>
            Attribution window <span style={{ fontWeight: 500, color: ADMIN_PALETTE.inkMuted, textTransform: 'none', letterSpacing: 0 }}>— how long after a ping a PAID scan still counts</span>
          </label>
          <FilterChips options={WINDOW_OPTIONS} active={windowHours} onChange={setWindowHours} testidPrefix="admin-en-route-window" />
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: ADMIN_PALETTE.inkMuted }}>Sort venues by</label>
          <FilterChips options={SORT_OPTIONS} active={sort} onChange={setSort} testidPrefix="admin-en-route-sort" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: ADMIN_PALETTE.inkMuted }}>Flag venue when pings ≥</label>
            <input
              type="number" min={1} max={1000}
              value={flagMinPings} onChange={(e) => setFlagMinPings(Math.max(1, Number(e.target.value) || 1))}
              data-testid="admin-en-route-flag-min-pings"
              style={{ padding: '8px 10px', borderRadius: 10, border: `1px solid ${ADMIN_PALETTE.border}`, fontSize: 13 }}
            />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: ADMIN_PALETTE.inkMuted }}>… and conversion &lt; (%)</label>
            <input
              type="number" min={0} max={100} step={1}
              value={flagMaxConvPct} onChange={(e) => setFlagMaxConvPct(Math.min(100, Math.max(0, Number(e.target.value) || 0)))}
              data-testid="admin-en-route-flag-max-conv"
              style={{ padding: '8px 10px', borderRadius: 10, border: `1px solid ${ADMIN_PALETTE.border}`, fontSize: 13 }}
            />
          </div>
          <div style={{ display: 'grid', gap: 6, gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: ADMIN_PALETTE.inkMuted }}>Search venue</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${ADMIN_PALETTE.border}`, borderRadius: 10, padding: '6px 10px' }}>
              <Search size={14} color={ADMIN_PALETTE.inkMuted} />
              <input
                value={venueSearch}
                onChange={(e) => setVenueSearch(e.target.value)}
                placeholder="Filter by venue name…"
                data-testid="admin-en-route-venue-search"
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── KPI strip ────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <KpiCard testid="admin-en-route-kpi-pings"
          label="Guests pinged" value={totals.total_pings ?? '—'}
          sub={`${totals.unique_pinging_guests ?? 0} unique guests`}
          tone="venue" />
        <KpiCard testid="admin-en-route-kpi-conversions"
          label="Actually scanned (PAID)" value={totals.total_conversions ?? '—'}
          sub={`${totals.unique_converted_guests ?? 0} unique guests`}
          tone="active" />
        <KpiCard testid="admin-en-route-kpi-conv-rate"
          label="Conversion rate" value={`${totals.conversion_rate ?? 0}%`}
          sub="Ping → PAID within window"
          tone={
            (totals.conversion_rate ?? 0) >= 40 ? 'active'
            : (totals.conversion_rate ?? 0) >= 15 ? 'hold' : 'urgent'
          } />
        <KpiCard testid="admin-en-route-kpi-flagged"
          label="Flagged venues" value={totals.flagged_venues ?? 0}
          sub={`≥ ${flagMinPings} pings · < ${flagMaxConvPct}% conv`}
          tone={(totals.flagged_venues ?? 0) > 0 ? 'urgent' : 'neutral'} />
        <KpiCard testid="admin-en-route-kpi-ghosts"
          label="Ghost guests" value={totals.ghost_guests ?? 0}
          sub="Pinged, never showed up"
          tone={(totals.ghost_guests ?? 0) > 0 ? 'hold' : 'neutral'} />
      </div>

      {/* ── Trend chart ──────────────────────────────────────────── */}
      <div style={cardStyle} data-testid="admin-en-route-trend-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18, color: ADMIN_PALETTE.ink }}>Daily trend</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: ADMIN_PALETTE.inkMuted }}>Pings vs converted scans</p>
          </div>
        </div>
        <div style={{ width: '100%', height: 220 }}>
          {loading && chartData.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: 60 }}>Loading…</div>
          ) : chartData.length ? (
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                <XAxis dataKey="dayLabel" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip cursor={{ fill: 'rgba(112, 63, 246, 0.05)' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="pings" name="Pings" fill={STATUS_PALETTE.venue.solid} radius={[6, 6, 0, 0]} />
                <Bar dataKey="conversions" name="Converted" fill={STATUS_PALETTE.active.solid} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', color: ADMIN_PALETTE.inkMuted, padding: 60, fontSize: 13 }}>No pings in this range yet.</div>
          )}
        </div>
      </div>

      {/* ── Venue leaderboard ────────────────────────────────────── */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <MapPin size={16} color={ADMIN_PALETTE.inkSoft} />
          <h3 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18, color: ADMIN_PALETTE.ink }}>Per-venue breakdown</h3>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: ADMIN_PALETTE.inkMuted }}>
            {venues.length} venue{venues.length === 1 ? '' : 's'}
          </span>
        </div>
        {venues.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="admin-en-route-venues-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: ADMIN_PALETTE.inkMuted, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  <th style={{ padding: '8px 10px' }}>Venue</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Pings</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Guests</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Scanned</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Conv %</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Last ping</th>
                </tr>
              </thead>
              <tbody>
                {venues.map((row) => (
                  <tr
                    key={row.venue_id}
                    data-testid={`admin-en-route-venue-row-${row.venue_id}`}
                    style={{
                      borderTop: `1px solid ${ADMIN_PALETTE.border}`,
                      background: row.flagged ? 'rgba(221,6,8,0.05)' : 'transparent',
                    }}
                  >
                    <td style={{ padding: '10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontWeight: 700, color: ADMIN_PALETTE.ink }}>{row.venue_name}</div>
                      {row.flagged ? (
                        <span
                          data-testid={`admin-en-route-venue-flag-${row.venue_id}`}
                          title={`Flagged: ${row.pings} pings · only ${row.conversion_rate}% converted`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 8px', borderRadius: 9999,
                            background: STATUS_PALETTE.urgent.soft, color: STATUS_PALETTE.urgent.text,
                            border: `1px solid ${STATUS_PALETTE.urgent.solid}44`, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3,
                          }}
                        >
                          <ShieldAlert size={11} /> LOW CONVERSION
                        </span>
                      ) : null}
                      {row.city ? (
                        <span style={{ fontSize: 11, color: ADMIN_PALETTE.inkMuted }}>· {row.city}</span>
                      ) : null}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 700 }}>{row.pings}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: ADMIN_PALETTE.inkMuted }}>{row.unique_guests}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 700, color: row.conversions ? STATUS_PALETTE.active.text : ADMIN_PALETTE.inkMuted }}>{row.conversions}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 700, color: row.flagged ? STATUS_PALETTE.urgent.text : row.conversion_rate >= 40 ? STATUS_PALETTE.active.text : ADMIN_PALETTE.inkSoft }}>
                      {row.conversion_rate}%
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', color: ADMIN_PALETTE.inkMuted, fontSize: 12 }}>
                      {formatDateShort(row.last_ping_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div data-testid="admin-en-route-venues-empty" style={{ padding: 24, textAlign: 'center', color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>
            No venues received a ping in this range.
          </div>
        )}
      </div>

      {/* ── Ghost guests ─────────────────────────────────────────── */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Ghost size={16} color={ADMIN_PALETTE.inkSoft} />
          <h3 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18, color: ADMIN_PALETTE.ink }}>Ghost guests</h3>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: ADMIN_PALETTE.inkMuted }}>
            Pinged ≥ 3× · never converted
          </span>
        </div>
        {ghosts.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="admin-en-route-ghosts-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: ADMIN_PALETTE.inkMuted, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  <th style={{ padding: '8px 10px' }}>Guest</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Pings</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Venues</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Last ping</th>
                </tr>
              </thead>
              <tbody>
                {ghosts.map((row) => (
                  <tr key={row.guest_id} data-testid={`admin-en-route-ghost-row-${row.guest_id}`} style={{ borderTop: `1px solid ${ADMIN_PALETTE.border}` }}>
                    <td style={{ padding: '10px' }}>
                      <div style={{ fontWeight: 700, color: ADMIN_PALETTE.ink }}>{row.guest_name}</div>
                      <div style={{ fontSize: 11, color: ADMIN_PALETTE.inkMuted }}>
                        {row.guest_email}{row.customer_id ? ` · ${row.customer_id}` : ''}
                        {row.deleted ? ' · deleted' : ''}
                      </div>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 700 }}>{row.pings}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: ADMIN_PALETTE.inkMuted }}>{row.venues_pinged}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: ADMIN_PALETTE.inkMuted, fontSize: 12 }}>{formatDateShort(row.last_ping_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div data-testid="admin-en-route-ghosts-empty" style={{ padding: 24, textAlign: 'center', color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>
            No ghost guests in this range. Good sign — every noisy pinger showed up.
          </div>
        )}
      </div>
    </div>
  );
};
